<template>
	<nav
		id="mobile-navigation"
		class="nav-mobile"
		:class="{'is-open': isOpen, 'child-is-open': openItemId !== null}"
		:aria-label="$Helpers.ucfirst($I18n.trans('commons.navigation'))"
		:aria-hidden="!isOpen"
		:inert="isOpen ? null : ''"
	>
		<div class="nav-mobile__header">
			<button
				v-if="mobileNavOpenItemIds.length"
				type="button"
				class="nav-mobile__back"
				@click="handleNavMobileBackClick"
			>
				<V2Icon
					icon-name="arrow-left"
					size="lg"
				/>
				<span>{{ $Helpers.ucfirst($I18n.trans('commons.back')) }}</span>
			</button>
			<button
				ref="closeButton"
				type="button"
				class="nav-mobile__toggle"
				:aria-label="$Helpers.ucfirst($I18n.trans('commons.close'))"
				@click="closeNavMobile"
			>
				<V2Icon
					icon-name="e-remove"
					size="lg"
				/>
			</button>
		</div>
		<div class="nav-mobile__content">
			<a
				v-for="item in visibleContextNavItems"
				:key="item.link"
				:href="item.link"
				:target="mixinIsExternalLink(item.link) ? '_blank' : '_self'"
				:rel="mixinIsExternalLink(item.link) ? 'noopener' : ''"
				class="nav-mobile__item"
				:class="{'is-active': item.isActive}"
				:aria-current="item.isActive ? 'page' : null"
			>
				<span>{{ item.text }}</span>
				<V2Icon
					v-if="mixinIsExternalLink(item.link)"
					icon-name="launch"
					size="xxs"
				/>
			</a>
			<template v-if="showMobileMenuNavigationItems && !$arenaConfig.prominentMobileTopNav">
				<a
					v-for="item in topNavItems"
					:key="item.link"
					:href="item.link"
					:target="mixinIsExternalLink(item.link) ? '_blank' : '_self'"
					:rel="mixinIsExternalLink(item.link) ? 'noopener' : ''"
					class="nav-mobile__item"
					:class="{'is-active': item.isActive}"
					:aria-current="item.isActive ? 'page' : null"
				>
					<span>{{ item.title }}</span>
					<V2Icon
						v-if="mixinIsExternalLink(item.link)"
						icon-name="launch"
						size="xxs"
					/>
				</a>
			</template>
			<NavMobileChild
				ref="navMobileChild"
				:sub-nav-items="subNavItems"
				@open-child="(navigationItemId) => handleNavMobileOpenChild(navigationItemId)"
			/>
		</div>
	</nav>
</template>

<script>
import V2Icon from '@shared/components/Icon';
import NavMobileChild from '@js/components/NavMobileChild.vue';

export default {
	name:       'NavMobile',
	components: { V2Icon, NavMobileChild },
	props:      {
		isOpen: {
			type:    Boolean,
			default: false,
		},
		contextNavItems: {
			type:    Array,
			default: () => [],
		},
		topNavItems: {
			type:    Array,
			default: () => [],
		},
		subNavItems: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			openItemId:           null,
			mobileNavOpenItemIds: [],
		};
	},
	computed: {
		showMobileMenuNavigationItems() {
			return this.$arenaConfig.includeTopNavigationInMobileMenu;
		},
		visibleContextNavItems() {
			if (!this.showMobileMenuNavigationItems) {
				return [];
			}

			return this.contextNavItems.filter(item => !item.hidden);
		},
	},
	watch: {
		isOpen(isOpenValue) {
			this.handleOpen(isOpenValue);
		},
	},
	mounted() {
		window.eventBus.$on('mobileNav:close-child', (data) => {
			if (data.id === this.openItemId) {
				this.openItemId = null;
			}
		});
	},
	methods: {
		handleOpen(isOpen) {
			if (isOpen) {
				document.documentElement.style.overflow = 'hidden';
			} else {
				document.documentElement.style.overflow = '';
				while (this.mobileNavOpenItemIds.length) {
					window.eventBus.$emit('mobileNav:close-child', { id: this.mobileNavOpenItemIds.pop() });
				}
				this.openItemId = null;
			}
		},
		focusCloseButton() {
			this.$refs.closeButton?.focus();
		},
		closeNavMobile() {
			this.$emit('close-nav-mobile');
		},
		handleNavMobileOpenChild(navigationItemId) {
			if (!this.mobileNavOpenItemIds.includes(navigationItemId)) {
				this.mobileNavOpenItemIds.push(navigationItemId);
			}
		},
		handleNavMobileBackClick() {
			const navigationItemId = this.mobileNavOpenItemIds.pop();
			window.eventBus.$emit('mobileNav:close-child', { id: navigationItemId });
			this.$nextTick(() => {
				this.$el.querySelector(`[aria-controls="mobile-nav-child-${navigationItemId}"]`)?.focus();
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.nav-mobile {
	display: flex;
	flex-direction: column;
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100vh;
	clip-path: inset(0 0 0 100%);
	transform: translateX(0%);
	transition: transform $trans-time-fast, clip-path $trans-time-fast;
	color: var(--color-white);
	background-color: var(--color-black);
	pointer-events: none;
	visibility: visible;

	&.is-open {
		clip-path: inset(0);
		transform: translateX(0%);
		pointer-events: auto;
	}

	&.child-is-open {
		clip-path: inset(0);
		transform: translateX(-20%);
		pointer-events: auto;
	}

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		color: var(--color-black);
	}

	// Hide mobile nav only on non-touch devices that are md-up or larger
	// This ensures it's visible when the mobile toggle is shown (touch devices or small screens)
	@media (min-width: 1024px) and (pointer: fine) {
		visibility: hidden;
	}
}

.nav-mobile__header {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	height: $sp7-5;
	background-color: var(--color-black);
	padding: $sp4;
	z-index: 2;

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		background-color: var(--color-white);
	}
}

.nav-mobile__toggle,
.nav-mobile__back {
	border: 0;
	background-color: transparent;
	color: inherit;
	cursor: pointer;
}

.nav-mobile__toggle {
	display: flex;
	padding: $sp2;
}

.nav-mobile__content {
	position: fixed;
	left: 0;
	top: $sp7-5;
	width: 100%;
	height: calc(100vh - #{$sp7-5});
	background-color: var(--color-black);
	overflow-y: scroll;
	overflow-x: hidden;
	overscroll-behavior: contain;
	-webkit-overflow-scrolling: touch;
	padding-bottom: 10vh;

	// &.is-open {}
	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		background-color: var(--color-white);
	}
}

// .nav-mobile__toggle {}

.nav-mobile__item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 0 $sp4;
	padding: $sp4 0;
	font-size: 16px; // stylelint-disable-line property-disallowed-list, sh-waqar/declaration-use-variable
	border-bottom: 1px solid var(--color-grey-900);
	cursor: pointer;
}

.nav-mobile__child {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--color-black);
	transform: translateX(100%);
	transition: transform $trans-time-fast;

	&.is-open {
		transform: translateX(20%);
		z-index: 1;
	}
}

.nav-mobile__back {
	display: flex;
	flex-grow: 1;
	align-items: center;
	padding: $sp2 0;
	gap: 0 $sp4;
	font-size: 16px; // stylelint-disable-line property-disallowed-list, sh-waqar/declaration-use-variable
	text-align: left;
}
</style>
